import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Paper,
  Card,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Skeleton,
} from '@mui/material';
import {
  Code as CodeIcon,
  Speed as SpeedIcon,
  Launch as LaunchIcon,
  Article as ArticleIcon,
  Security as SecurityIcon,
  Download as DownloadIcon,
  ContentCopy as ContentCopyIcon,
  PlayCircle as PlayCircleIcon,
  School as SchoolIcon,
  Collections as GalleryIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import useAuth from '../../features/auth/hooks/useAuth';
import { RELEASE_CONFIG } from '../../config/releases';
import GalleryCard from '../../features/gallery/components/GalleryCard';
import ReviewsCarousel from '../../features/reviews/components/ReviewsCarousel';
import { fetchGalleryItems, GalleryItem } from '../../features/gallery/api/galleryApi';
import { brand } from '../../config/brand';

// YouTube video ID for the product demo
const YOUTUBE_VIDEO_ID = 'LQ2FRwUIuwI';

const HomePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated } = useAuth();
  const [installTab, setInstallTab] = useState(0);
  const [copied, setCopied] = useState(false);
  const [featuredItems, setFeaturedItems] = useState<GalleryItem[]>([]);
  const [galleryLoading, setGalleryLoading] = useState(true);

  useEffect(() => {
    fetchGalleryItems({ limit: 4, sort: 'popular' })
      .then((res) => setFeaturedItems(res.items))
      .catch(() => {})
      .finally(() => setGalleryLoading(false));
  }, []);

  const handleCopyCommand = () => {
    navigator.clipboard.writeText(RELEASE_CONFIG.npm.installCommand);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box>
      {/* Hero Section with Video Background */}
      <Box
        sx={{
          position: 'relative',
          color: 'white',
          py: { xs: 4, md: 6 },
          minHeight: { xs: 'calc(100vh - 56px)', md: 'calc(100vh - 64px)' },
          maxHeight: { xs: 'auto', md: 'calc(100vh - 64px)' },
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        {/* Video Background */}
        <Box
          component="video"
          autoPlay
          muted
          loop
          playsInline
          poster="/img/landscape.jpeg"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        >
          <source src="/video/landscape.mp4" type="video/mp4" />
        </Box>

        {/* Gradient overlay (dims video, semi-transparent so video shows through) */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: brand.gradients.hero,
            opacity: 0.85,
            zIndex: 1,
          }}
        />

        {/* Content */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={3} alignItems="center">
            {/* Title - Always first */}
            <Grid item xs={12} md={7} order={{ xs: 1, md: 1 }}>
              <Typography
                variant="h1"
                component="h1"
                fontWeight="bold"
                sx={{
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
                  lineHeight: 1.15,
                  mb: { xs: 2, md: 2 },
                  color: 'white',
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                What If You Could Ask Your Dev Tool For An F-16 Flight Simulator?
              </Typography>
            </Grid>

            {/* YouTube Video - Second on mobile, last on desktop */}
            <Grid item xs={12} md={5} order={{ xs: 2, md: 3 }}>
              <Box
                sx={{
                  position: 'relative',
                  paddingTop: '56.25%', // 16:9 aspect ratio
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
                }}
              >
                <Box
                  component="iframe"
                  src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?rel=0&modestbranding=1`}
                  title={`${brand.fullProductName} Demo`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                />
              </Box>
            </Grid>

            {/* Rest of content - Third on mobile, second on desktop */}
            <Grid item xs={12} md={7} order={{ xs: 3, md: 2 }}>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 3,
                    color: 'white',
                    opacity: 0.95,
                    fontSize: { xs: '0.95rem', md: '1.1rem' },
                    lineHeight: 1.5,
                    maxWidth: 550,
                    textAlign: { xs: 'center', md: 'left' },
                  }}
                >
                  {brand.fullProductName} is a multiagent platform aimed for generating high-quality,
                  production-grade code that targets web and mobile applications.
                </Typography>

                {/* Install Options */}
                <Paper
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 2,
                    overflow: 'hidden',
                    maxWidth: { xs: '100%', md: 500 },
                  }}
                >
                  <Tabs
                    value={installTab}
                    onChange={(_, v) => setInstallTab(v)}
                    sx={{
                      minHeight: 42,
                      bgcolor: 'rgba(0,0,0,0.2)',
                      '& .MuiTab-root': {
                        color: 'rgba(255,255,255,0.7)',
                        fontWeight: 600,
                        minHeight: 42,
                        py: 1,
                        '&.Mui-selected': { color: 'white' },
                      },
                      '& .MuiTabs-indicator': { bgcolor: 'white' },
                    }}
                  >
                    <Tab label="Download" />
                    <Tab label="NPM" />
                  </Tabs>

                  <Box sx={{ p: 1.5 }}>
                    {installTab === 0 ? (
                      <Box>
                        <Grid container spacing={1}>
                          {Object.values(RELEASE_CONFIG.binaries).map((binary) => (
                            <Grid item xs={4} key={binary.label}>
                              <Button
                                fullWidth
                                variant="contained"
                                href={binary.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                startIcon={
                                  <Box
                                    component="img"
                                    src={binary.icon}
                                    alt={binary.label}
                                    sx={{
                                      width: 18,
                                      height: 18,
                                      filter: 'brightness(0) invert(1)',
                                      display: { xs: 'none', sm: 'block' },
                                    }}
                                  />
                                }
                                sx={{
                                  bgcolor: 'rgba(255,255,255,0.15)',
                                  color: 'white',
                                  py: 1,
                                  fontSize: { xs: '0.75rem', sm: '0.85rem' },
                                  '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                                }}
                              >
                                {binary.label}
                              </Button>
                            </Grid>
                          ))}
                        </Grid>
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.8, fontSize: '0.7rem' }}>
                          Version {RELEASE_CONFIG.version} •
                          <Box
                            component="a"
                            href={RELEASE_CONFIG.github.releasesUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: 'inherit', ml: 0.5, textDecoration: 'underline' }}
                          >
                            View all releases
                          </Box>
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: 'rgba(0,0,0,0.3)',
                            borderRadius: 1,
                            p: 1,
                            fontFamily: 'monospace',
                          }}
                        >
                          <Typography
                            sx={{
                              flexGrow: 1,
                              fontSize: '0.85rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            $ {RELEASE_CONFIG.npm.installCommand}
                          </Typography>
                          <Tooltip title={copied ? 'Copied!' : 'Copy command'}>
                            <IconButton
                              size="small"
                              onClick={handleCopyCommand}
                              sx={{ color: 'white', ml: 1 }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8, fontSize: '0.7rem' }}>
                          Requires Node.js 18+. Run <code>{brand.storagePrefix} web</code> after install.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>

                {/* CTA Buttons */}
                <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <Button
                    variant="contained"
                    size="medium"
                    component={RouterLink}
                    to={isAuthenticated ? '/dashboard' : '/register'}
                    sx={{
                      bgcolor: 'white',
                      color: 'primary.main',
                      px: 3,
                      py: 1,
                      fontWeight: 600,
                      '&:hover': { bgcolor: 'grey.100' },
                    }}
                  >
                    {isAuthenticated ? 'Go to Dashboard' : 'Get Started Free'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="medium"
                    component={RouterLink}
                    to="/training"
                    startIcon={<SchoolIcon />}
                    sx={{
                      borderColor: 'white',
                      color: 'white',
                      px: 3,
                      py: 1,
                      fontWeight: 600,
                      '&:hover': { borderColor: 'grey.300', bgcolor: 'rgba(255,255,255,0.1)' },
                    }}
                  >
                    Watch Tutorials
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* How Autopilot One Works */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" mb={6}>
          <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
            Multi-Agent Intelligence, Unified Experience
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
            {brand.productName} orchestrates specialized AI agents working in harmony—each expert in their domain—to deliver secure,
            production-ready code that follows your project's conventions and best practices.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
              <Box sx={{
                bgcolor: 'primary.main',
                width: 80,
                height: 80,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2
              }}>
                <SecurityIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography variant="h5" gutterBottom fontWeight="600">
                Security-First Development
              </Typography>
              <Typography color="text.secondary">
                Every line of code is built with security in mind. Our multi-agent system automatically
                implements best practices, vulnerability prevention, and secure coding patterns to protect your applications.
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
              <Box sx={{
                bgcolor: 'secondary.main',
                width: 80,
                height: 80,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2
              }}>
                <CodeIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography variant="h5" gutterBottom fontWeight="600">
                Maintains Your Standards
              </Typography>
              <Typography color="text.secondary">
                Analyzes and adapts to your project's existing coding style, conventions, and best practices—
                delivering code that feels native to your codebase, making it easier to trust and integrate.
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
              <Box sx={{
                bgcolor: 'success.main',
                width: 80,
                height: 80,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2
              }}>
                <SpeedIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography variant="h5" gutterBottom fontWeight="600">
                Fast, Production-Ready Deployment
              </Typography>
              <Typography color="text.secondary">
                From concept to deployed application in minutes. Generate production-ready code with
                built-in testing, documentation, and deployment configurations—ready to ship immediately.
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Documentation Hub */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
              Ready to Start Building?
            </Typography>
            <Typography variant="h6" color="text.secondary" paragraph>
              Get started with {brand.productName} in minutes. Modern Web UI + powerful CLI for any workflow.
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom color="primary.main">
                Quick Start:
              </Typography>
              <Box component="ol" sx={{ pl: 2 }}>
                <Typography component="li" sx={{ mb: 1.5 }}>
                  <strong>Create Account:</strong> Register at <code>{brand.domain}</code>
                </Typography>
                <Typography component="li" sx={{ mb: 1.5 }}>
                  <strong>Install:</strong> <code>{RELEASE_CONFIG.npm.installCommand}</code>
                </Typography>
                <Typography component="li" sx={{ mb: 1.5 }}>
                  <strong>Get API Key:</strong> Navigate to API Keys section and create a new key
                </Typography>
                <Typography component="li" sx={{ mb: 1.5 }}>
                  <strong>Launch:</strong> Run <code>{brand.storagePrefix} web</code> for Web UI or <code>{brand.storagePrefix} plus-terminal</code> for Terminal UI
                </Typography>
                <Typography component="li" sx={{ mb: 1.5 }}>
                  <strong>Configure:</strong> Go to Settings and paste your API key
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                href={brand.links.npm}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<ArticleIcon />}
                sx={{ mb: 1 }}
              >
                View Documentation
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={RouterLink}
                to="/training"
                startIcon={<PlayCircleIcon />}
                sx={{ mb: 1 }}
              >
                Video Tutorials
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                bgcolor: '#1e1e1e',
                color: '#d4d4d4',
                p: 3,
                borderRadius: 2,
                fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                fontSize: '0.85rem',
                border: '1px solid #333',
              }}
            >
              <Typography component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap' }}>
{`# Install ${brand.productName}
$ ${RELEASE_CONFIG.npm.installCommand}

# Verify installation
$ ${brand.storagePrefix} --version
${brand.fullProductName} v${RELEASE_CONFIG.version}

# Launch Web UI (opens http://localhost:8080)
$ ${brand.storagePrefix} web

# Or launch Terminal UI
$ ${brand.storagePrefix} plus-terminal

# CLI Reference
${brand.storagePrefix} web             # Web UI in browser
${brand.storagePrefix} terminal        # Terminal UI only
${brand.storagePrefix} plus-terminal   # Server + Terminal UI
${brand.storagePrefix} --port 3000     # Custom port`}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Community App Gallery Showcase */}
      <Box sx={{ bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'background.paper', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: `rgba(${brand.colorsRgb.fire}, 0.1)`,
                border: `1px solid rgba(${brand.colorsRgb.fire}, 0.2)`,
                borderRadius: 5,
                px: 2,
                py: 0.5,
                mb: 2,
              }}
            >
              <GalleryIcon sx={{ fontSize: 18, color: brand.colors.fire }} />
              <Typography variant="caption" fontWeight={700} sx={{ color: brand.colors.fire, letterSpacing: 1, textTransform: 'uppercase' }}>
                Community Showcase
              </Typography>
            </Box>
            <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
              Community App Gallery
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Discover amazing applications built with {brand.productName} by our community of developers.
            </Typography>
          </Box>

          {galleryLoading ? (
            <Grid container spacing={3} justifyContent="center">
              {Array.from({ length: 4 }).map((_, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
                </Grid>
              ))}
            </Grid>
          ) : featuredItems.length > 0 ? (
            <Grid container spacing={3} justifyContent="center">
              {featuredItems.map((item) => (
                <Grid item xs={12} sm={6} md={3} key={item._id}>
                  <GalleryCard item={item} />
                </Grid>
              ))}
            </Grid>
          ) : null}

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 5, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to="/gallery"
              endIcon={<ArrowForwardIcon />}
              sx={{
                px: 4,
                py: 1.5,
                fontWeight: 700,
                background: brand.gradients.button,
                color: brand.buttonTextColor,
                '&:hover': { background: brand.gradients.buttonHover },
              }}
            >
              Browse Gallery
            </Button>
            <Button
              variant="outlined"
              size="large"
              component={RouterLink}
              to={isAuthenticated ? '/gallery/submit' : '/register'}
              startIcon={<AddIcon />}
              sx={{
                px: 4,
                py: 1.5,
                fontWeight: 700,
                borderColor: brand.colors.fire,
                color: brand.colors.fire,
                '&:hover': { borderColor: brand.colors.fireDark, bgcolor: `rgba(${brand.colorsRgb.fire}, 0.05)` },
              }}
            >
              Submit Your App
            </Button>
          </Box>
        </Container>
      </Box>

      {/* What Our Users Say - Reviews Carousel */}
      <ReviewsCarousel />

      {/* Pricing Plans Preview */}
      <Box sx={{ bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'background.paper', py: 10 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={5}>
            <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
              Simple, Transparent Pricing
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Start free and scale as you grow. All plans include access to every AI model.
            </Typography>
          </Box>

          <Grid container spacing={3} justifyContent="center">
            {[
              { key: 'scout', name: 'Scout', price: 'Free', credits: '100K', desc: 'Get started', color: '#78909c', highlight: false },
              { key: 'cadet', name: 'Cadet', price: '$19.99', credits: '800K', desc: 'First month free', color: '#42a5f5', highlight: false },
              { key: 'captain', name: 'Captain', price: '$49.99', credits: '2M', desc: 'Most popular', color: brand.colors.fire, highlight: true },
              { key: 'air_marshal', name: 'Air Marshal', price: '$149.99', credits: '6.6M', desc: 'Power users', color: '#ab47bc', highlight: false },
            ].map((plan) => (
              <Grid item xs={6} sm={6} md={3} key={plan.key}>
                <Box
                  component={RouterLink}
                  to="/pricing"
                  sx={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                >
                  <Paper
                    elevation={plan.highlight ? 6 : 1}
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      border: plan.highlight ? `2px solid ${plan.color}` : '1px solid',
                      borderColor: plan.highlight ? plan.color : 'divider',
                      position: 'relative',
                      overflow: 'visible',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: 6,
                      },
                    }}
                  >
                    {plan.highlight && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -12,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          bgcolor: plan.color,
                          color: 'white',
                          px: 2,
                          py: 0.3,
                          borderRadius: 1,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        MOST POPULAR
                      </Box>
                    )}
                    <Typography variant="overline" sx={{ color: plan.color, fontWeight: 700, letterSpacing: 1 }}>
                      {plan.name}
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ my: 1 }}>
                      {plan.price}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {plan.price !== 'Free' ? '/month' : 'forever'}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: plan.color }}>
                      {plan.credits} credits/mo
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {plan.desc}
                    </Typography>
                  </Paper>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box textAlign="center" mt={4}>
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to="/pricing"
              endIcon={<ArrowForwardIcon />}
              sx={{
                px: 5,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 700,
                background: brand.gradients.button,
                color: brand.buttonTextColor,
                '&:hover': {
                  background: brand.gradients.buttonHover,
                },
              }}
            >
              Compare Plans & Features
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Final CTA */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box textAlign="center">
          <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
            Start Building with {brand.productName} Today
          </Typography>
          <Typography variant="h6" color="text.secondary" paragraph>
            Join developers who are shipping secure, production-ready applications faster with multi-agent AI intelligence.
          </Typography>

          <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to={isAuthenticated ? "/dashboard" : "/register"}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 700,
                background: brand.gradients.button,
                color: brand.buttonTextColor,
                '&:hover': { background: brand.gradients.buttonHover },
              }}
            >
              {isAuthenticated ? "Go to Dashboard" : "Get Started Free"}
            </Button>
            <Button
              variant="outlined"
              size="large"
              href={brand.links.npm}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 700,
                borderColor: brand.colors.fire,
                color: brand.colors.fire,
                '&:hover': { borderColor: brand.colors.fireDark, bgcolor: `rgba(${brand.colorsRgb.fire}, 0.05)` },
              }}
            >
              Read the Docs
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HomePage;
